/**
 * Admin → Dealer desk → Online: who is connected right now. Clients from their own device (page,
 * since when, open lots and P&L, a team member logged in as them), the team (Admin sessions, from
 * the heartbeat below; CRM staff logged in as a client), and clients with open positions who are
 * offline. Refreshes every 10 s.
 */
import { useEffect, useState } from 'react';
import { D, fmtDateTime, fmtUsd } from '@platform/shared';
import { cx, Empty, Spinner } from '../components/ui';
import { api } from '../lib/api';
import { useLoad } from '../stores/account';

const REFRESH_MS = 10_000;

/** Tell the server this Admin session is open and where it is, every 10 s (the Online tab's team list). */
export function useTeamHeartbeat(where: string) {
  useEffect(() => {
    let gone = false;
    const beat = () => {
      const account = new URLSearchParams(window.location.search).get('account');
      void api.post('/api/admin/desk/heartbeat', { where: account ? `${where} · ${account.slice(0, 8)}` : where }).catch(() => {});
    };
    beat();
    const t = window.setInterval(() => !gone && beat(), REFRESH_MS);
    return () => {
      gone = true;
      window.clearInterval(t);
    };
  }, [where]);
}

interface Client {
  userId: string;
  accountId: string;
  name: string | null;
  externalClientId: string | null;
  online: boolean;
  page: string | null;
  lastSeenAt: string | null;
  openPositions: number;
  openLots: string;
  openPnl: string;
  equity: string;
  freeMargin: string;
  marginLevel: string | null;
  staff: { name: string; page: string | null; since: string } | null;
}
interface Team {
  staffId: string;
  name: string;
  role: string;
  via: 'admin' | 'crm';
  where: string;
  since: string | null;
  actionsToday: number;
}
interface Online {
  asOf: string;
  clients: Client[];
  team: Team[];
  offline: Client[];
}

const td = 'px-3 py-2 text-[12.5px] whitespace-nowrap';
const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const num = 'num text-right';
const tone = (x: string) => (D.from(x).isNeg() ? 'text-down' : D.from(x).isPos() ? 'text-up' : '');
const signed = (x: string) => `${D.from(x).isNeg() ? '−' : D.from(x).isPos() ? '+' : ''}${fmtUsd(D.from(x).abs())}`;
const ROLE: Record<string, string> = { support: 'Support', dealer: 'Dealer', senior_dealer: 'Senior dealer', finance: 'Finance', compliance: 'Compliance', admin: 'Admin', retention_dealer: 'Retention dealer' };
const PAGE: Record<string, string> = { trade: 'Trade', wallet: 'Wallet', deposit: 'Deposit', withdraw: 'Withdraw', kyc: 'KYC', support: 'Support', portfolio: 'Portfolio', account: 'Account', other: 'Other page' };
const sinceLabel = (iso: string | null) => {
  if (!iso) return '—';
  const min = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  return `${fmtDateTime(iso).split(', ')[1] ?? fmtDateTime(iso)} (${min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${min % 60} min`})`;
};
const clientLabel = (c: Client) => c.name ?? c.externalClientId ?? c.accountId.slice(0, 8);

export function OnlineTab() {
  const [list, setList] = useState<'online' | 'offline'>('online');
  const { data, error, reload, loading } = useLoad<Online>('/api/admin/desk/online');
  useEffect(() => {
    const t = window.setInterval(() => void reload(), REFRESH_MS);
    return () => window.clearInterval(t);
  }, [reload]);
  const openAccount = (c: Client) => {
    window.history.pushState({}, '', `/admin?tab=accounts&account=${c.accountId}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  if (error) return <p className="m-0 text-xs text-down">{error}</p>;
  if (!data) return <Spinner />;
  const clients = list === 'online' ? data.clients : data.offline;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-lg font-semibold">Online now</h2>
        <span className="text-xs text-muted">
          {data.clients.length} clients · {data.team.length} team · refreshes every 10 s {loading && <Spinner />}
        </span>
      </div>

      <section className="flex flex-col gap-2">
        <div role="tablist" className="flex gap-1">
          {(['online', 'offline'] as const).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={list === k} className={cx('h-8 rounded-lg border px-3 text-xs font-semibold', list === k ? 'border-accent bg-accent/10 text-fg' : 'border-line text-muted')} onClick={() => setList(k)}>
              {k === 'online' ? `Clients online (${data.clients.length})` : `Offline with open positions (${data.offline.length})`}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto rounded-xl border border-line bg-panel">
          <table className="w-full border-collapse" style={{ minWidth: 980 }}>
            <thead>
              <tr className="border-b border-line">
                <th className={th}>Client</th>
                <th className={th}>CRM ID</th>
                <th className={th}>{list === 'online' ? 'Where' : 'Last seen'}</th>
                <th className={th}>{list === 'online' ? 'Since' : ''}</th>
                <th className={cx(th, 'text-right')}>Open positions</th>
                <th className={cx(th, 'text-right')}>Open lots</th>
                <th className={cx(th, 'text-right')}>Open P&amp;L</th>
                <th className={cx(th, 'text-right')}>Equity</th>
                <th className={cx(th, 'text-right')}>Margin level</th>
                <th className={th}>Team on the account</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.userId} className="cursor-pointer border-b border-line hover:bg-raised/50" onClick={() => openAccount(c)}>
                  <td className={cx(td, 'font-semibold')}>
                    <span className={cx('mr-1.5', c.online ? 'text-up' : 'text-muted')}>{c.online ? '●' : '○'}</span>
                    {clientLabel(c)}
                  </td>
                  <td className={cx(td, 'num text-muted')}>{c.externalClientId ?? '—'}</td>
                  <td className={td}>{list === 'online' ? (c.page ? (PAGE[c.page] ?? c.page) : '—') : c.lastSeenAt ? fmtDateTime(c.lastSeenAt) : 'never'}</td>
                  <td className={cx(td, 'num text-muted')}>{list === 'online' ? sinceLabel(c.lastSeenAt) : ''}</td>
                  <td className={cx(td, num)}>{c.openPositions}</td>
                  <td className={cx(td, num)}>{c.openLots}</td>
                  <td className={cx(td, num, tone(c.openPnl))}>{signed(c.openPnl)}</td>
                  <td className={cx(td, num)}>{fmtUsd(c.equity)}</td>
                  <td className={cx(td, num)}>{c.marginLevel ? `${D.from(c.marginLevel).mul(100).toFixed(1)}%` : '—'}</td>
                  <td className={cx(td, 'text-accent')}>{c.staff ? `${c.staff.name} · logged in as client since ${sinceLabel(c.staff.since)}` : <span className="text-muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!clients.length && <Empty>{list === 'online' ? 'No client is online right now.' : 'Every client with an open position is online.'}</Empty>}
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="m-0 text-sm font-semibold">Team online ({data.team.length})</h3>
        <div className="overflow-x-auto rounded-xl border border-line bg-panel">
          <table className="w-full border-collapse" style={{ minWidth: 760 }}>
            <thead>
              <tr className="border-b border-line">
                <th className={th}>Name</th>
                <th className={th}>Role</th>
                <th className={th}>Where</th>
                <th className={th}>Since</th>
                <th className={th}>Via</th>
                <th className={cx(th, 'text-right')}>Actions today</th>
              </tr>
            </thead>
            <tbody>
              {data.team.map((t) => (
                <tr key={t.staffId} className="border-b border-line">
                  <td className={cx(td, 'font-semibold')}>
                    <span className="mr-1.5 text-up">●</span>
                    {t.name}
                  </td>
                  <td className={td}>{ROLE[t.role] ?? t.role}</td>
                  <td className={td}>{t.where}</td>
                  <td className={cx(td, 'num text-muted')}>{sinceLabel(t.since)}</td>
                  <td className={td}>{t.via === 'crm' ? 'CRM' : 'Admin'}</td>
                  <td className={cx(td, num)}>{t.actionsToday}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data.team.length && <Empty>No team member is connected.</Empty>}
        </div>
      </section>
      <p className="m-0 text-[11px] text-muted">As of {fmtDateTime(data.asOf)}. A client counts as online from their own device; a team member logged in as them is listed on the right.</p>
    </div>
  );
}
